import { useState } from 'react';
import {
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem, Divider,
  Drawer, List, ListItemButton, ListItemIcon, ListItemText, Box,
  useMediaQuery, useTheme, ListItem,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, AttachMoney, Description,
  TrendingUp, MenuBook, Groups, Message as MessageIcon, Logout, School, Analytics,
  DirectionsBus, LocalLibrary,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const DRAWER_W = 250;

const NAV = [
  { label: 'Dashboard',      icon: <DashboardIcon />, to: '/parent' },
  { label: 'School Fees',    icon: <AttachMoney />,   to: '/parent/fees' },
  { label: 'Fee Statements', icon: <Description />,   to: '/parent/statements' },
  { label: 'Results',        icon: <TrendingUp />,    to: '/parent/assessments' },
  { label: 'LearnAlytics',   icon: <Analytics />,     to: '/parent/lernalytics' },
  { label: 'Academics',      icon: <MenuBook />,      to: '/parent/academics' },
  { label: 'Class Teachers', icon: <Groups />,        to: '/parent/teachers' },
  { label: 'School Bus',     icon: <DirectionsBus />, to: '/parent/transport' },
  { label: 'Library Books',  icon: <LocalLibrary />,  to: '/parent/books' },
  { label: 'Messages',       icon: <MessageIcon />,   to: '/parent/messages' },
];

export default function ParentLayout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acctEl, setAcctEl] = useState(null);

  const user   = JSON.parse(localStorage.getItem('user') || '{}');
  const school = JSON.parse(localStorage.getItem('school') || '{}');

  const go = (to) => { setMobileOpen(false); navigate(to); };

  const signOut = () => {
    ['token', 'user', 'school', 'permissions'].forEach(k => localStorage.removeItem(k));
    navigate('/login');
  };

  const isActive = (to) => to === '/parent'
    ? location.pathname === '/parent'
    : location.pathname.startsWith(to);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ gap: 1 }}>
        <School color="primary" />
        <Box>
          <Typography fontWeight={800} lineHeight={1.1}>Parent Portal</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {school.name || ''}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {NAV.map(item => (
          <ListItemButton key={item.to} onClick={() => go(item.to)}
            selected={isActive(item.to)}
            sx={{ borderRadius: 2, mb: 0.5,
              '&.Mui-selected': { bgcolor: 'primary.main', color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '& .MuiListItemIcon-root': { color: 'white' } } }}>
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1 }}>
        <ListItemButton onClick={signOut} sx={{ borderRadius: 2 }}>
          <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
          <ListItemText primary="Sign Out" primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f4f6f8' }}>
      {/* Top bar */}
      <AppBar position="fixed" elevation={1}
        sx={{ zIndex: theme.zIndex.drawer + 1, bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(o => !o)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/parent')} flex={1}>
            <Typography fontWeight={800} color="primary" lineHeight={1.1}>
              {school.name || 'Parent Portal'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Parent Portal</Typography>
          </Box>

          {/* Account */}
          <IconButton onClick={(e) => setAcctEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 700 }}>
              {(user.fullName || user.email || '?').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={acctEl} open={Boolean(acctEl)} onClose={() => setAcctEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <ListItem sx={{ pt: 0.5, pb: 1 }}>
              <Box>
                <Typography fontWeight={700} variant="body2">{user.fullName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
              </Box>
            </ListItem>
            <Divider />
            <MenuItem onClick={() => { setAcctEl(null); navigate('/parent'); }}>
              <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon> Dashboard
            </MenuItem>
            <MenuItem onClick={signOut} sx={{ color: 'error.main' }}>
              <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Side menu — permanent on desktop, swipe-in on mobile */}
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_W, flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_W, boxSizing: 'border-box', borderRight: '1px solid #eee' },
        }}>
        {drawerContent}
      </Drawer>

      {/* Page content */}
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_W}px)` } }}>
        <Toolbar /> {/* offset for the fixed AppBar */}
        {children}
      </Box>
    </Box>
  );
}
